import React, { useState } from 'react';
import {
    useTranslate,
} from 'react-admin';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    FormControlLabel,
    Switch,
    Chip,
    Stack,
    Alert,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Email as EmailIcon,
    Sms as SmsIcon,
    WhatsApp as WhatsAppIcon,
    NotificationsActive as PushIcon,
    Chat as SlackIcon,
} from '@mui/icons-material';

interface MemberChannelOverrideProps {
    open: boolean;
    member: any;
    groupChannels: string[];
    onClose: () => void;
    onSave: (channels: string[] | null, active: boolean) => void;
}

const channelIcons: Record<string, React.ReactElement> = {
    email: <EmailIcon sx={{ fontSize: 16 }} />,
    sms: <SmsIcon sx={{ fontSize: 16 }} />,
    whatsapp: <WhatsAppIcon sx={{ fontSize: 16 }} />,
    push: <PushIcon sx={{ fontSize: 16 }} />,
    slack: <SlackIcon sx={{ fontSize: 16 }} />,
};

export const MemberChannelOverride: React.FC<MemberChannelOverrideProps> = ({
    open,
    member,
    groupChannels,
    onClose,
    onSave,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const translate = useTranslate();

    const [useOverride, setUseOverride] = useState(!!member.channelsOverride);
    const [channels, setChannels] = useState<string[]>(
        member.channelsOverride || groupChannels
    );
    const [active, setActive] = useState(member.active !== false);
    
    const handleToggleChannel = (channel: string) => {
        setChannels(prev => {
            if (prev.includes(channel)) {
                return prev.filter(c => c !== channel);
            } else {
                return [...prev, channel];
            }
        });
    };
    
    const handleSave = () => {
        onSave(useOverride ? channels : null, active);
    };
    
    const handleReset = () => {
        setUseOverride(false);
        setChannels(groupChannels);
    };
    
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
            <DialogTitle sx={{ wordBreak: 'break-word' }}>
                {translate('resources.notification-groups.members.editDialog.title')}
            </DialogTitle>
            <DialogContent sx={{ px: isMobile ? 2 : 3 }}>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        {member.userName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {member.userEmail}
                    </Typography>
                </Box>
                
                <Box sx={{ mb: 3 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={active}
                                onChange={(e) => setActive(e.target.checked)}
                            />
                        }
                        label={translate('resources.notification-groups.members.editDialog.active')}
                    />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                        {translate('resources.notification-groups.members.editDialog.activeHelp')}
                    </Typography>
                </Box>
                
                <Box sx={{ p: 2, backgroundColor: '#F5F5F5', borderRadius: 1 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={useOverride}
                                onChange={(e) => {
                                    setUseOverride(e.target.checked);
                                    if (!e.target.checked) {
                                        setChannels(groupChannels);
                                    }
                                }}
                            />
                        }
                        label={translate('resources.notification-groups.members.editDialog.overrideChannels')}
                    />
                    
                    {!useOverride && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            {translate('resources.notification-groups.members.editDialog.usingGroupChannels')}
                        </Alert>
                    )}
                    
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            {useOverride
                                ? translate('resources.notification-groups.members.editDialog.selectChannels')
                                : translate('resources.notification-groups.members.editDialog.groupChannels')}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1, rowGap: 1 }}>
                            {['email', 'sms', 'whatsapp', 'push', 'slack'].map(channel => (
                                <Chip
                                    key={channel}
                                    icon={channelIcons[channel]}
                                    label={translate(`resources.notification-groups.channels.${channel}`)}
                                    onClick={useOverride ? () => handleToggleChannel(channel) : undefined}
                                    color={channels.includes(channel) ? 'primary' : 'default'}
                                    variant={channels.includes(channel) ? 'filled' : 'outlined'}
                                    disabled={!useOverride}
                                />
                            ))}
                        </Stack>
                    </Box>
                    
                    {useOverride && (
                        <Box sx={{ mt: 2 }}>
                            <Button size="small" onClick={handleReset}>
                                {translate('resources.notification-groups.members.editDialog.resetToGroup')}
                            </Button>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>
                    {translate('ra.action.cancel')}
                </Button>
                <Button 
                    onClick={handleSave} 
                    variant="contained"
                    disabled={useOverride && channels.length === 0}
                >
                    {translate('ra.action.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};